import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth tokens if needed
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: redirect to login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication
export const authAPI = {
  getCurrentUser: () => api.get('/auth/me'),
  getLoginUrl: (returnUrl?: string) => api.get('/auth/login', { params: { returnUrl } }),
  logout: () => api.post('/auth/logout'),
};

// Users
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id: number | string) => api.get(`/users/${id}`),
  create: (data: object) => api.post('/users', data),
  update: (id: number | string, data: object) => api.put(`/users/${id}`, data),
  delete: (id: number | string) => api.delete(`/users/${id}`),
};

// Audit Log
export const auditLogAPI = {
  getAll: (params?: object) => api.get('/audit-log', { params }),
  getEntityTypes: () => api.get('/audit-log/entity-types'),
};

// Providers
export const providersAPI = {
  getAll: () => api.get('/providers'),
  getById: (id: number | string) => api.get(`/providers/${id}`),
  create: (data: object) => api.post('/providers', data),
  update: (id: number | string, data: object) => api.put(`/providers/${id}`, data),
  delete: (id: number | string) => api.delete(`/providers/${id}`),
};

// Regions
export const regionsAPI = {
  getAll: (providerId?: number) => api.get('/regions', { params: { providerId } }),
  getById: (id: number | string) => api.get(`/regions/${id}`),
  create: (data: object) => api.post('/regions', data),
  update: (id: number | string, data: object) => api.put(`/regions/${id}`, data),
  delete: (id: number | string) => api.delete(`/regions/${id}`),
};

// VM Types
export const vmTypesAPI = {
  getAll: () => api.get('/vm-types'),
  getById: (id: number | string) => api.get(`/vm-types/${id}`),
  create: (data: object) => api.post('/vm-types', data),
  update: (id: number | string, data: object) => api.put(`/vm-types/${id}`, data),
  delete: (id: number | string) => api.delete(`/vm-types/${id}`),
};

// OS Types
export const osTypesAPI = {
  getAll: () => api.get('/os-types'),
  getById: (id: number | string) => api.get(`/os-types/${id}`),
  create: (data: object) => api.post('/os-types', data),
  update: (id: number | string, data: object) => api.put(`/os-types/${id}`, data),
  delete: (id: number | string) => api.delete(`/os-types/${id}`),
};

// Addons
export const addonsAPI = {
  getAll: () => api.get('/addons'),
  getById: (id: number | string) => api.get(`/addons/${id}`),
  create: (data: object) => api.post('/addons', data),
  update: (id: number | string, data: object) => api.put(`/addons/${id}`, data),
  delete: (id: number | string) => api.delete(`/addons/${id}`),
};

// Environments
export const environmentsAPI = {
  getAll: () => api.get('/environments'),
  getById: (id: number | string) => api.get(`/environments/${id}`),
  create: (data: object) => api.post('/environments', data),
  update: (id: number | string, data: object) => api.put(`/environments/${id}`, data),
  delete: (id: number | string) => api.delete(`/environments/${id}`),
};

// Default Addons
export const defaultAddonsAPI = {
  getAll: (providerId?: number, regionId?: number) => api.get('/default-addons', { params: { providerId, regionId } }),
  save: (data: object) => api.post('/default-addons', data),
  batchSave: (updates: object[]) => api.post('/default-addons/batch', { updates }),
  delete: (id: number | string) => api.delete(`/default-addons/${id}`),
};

// Pricing
export const pricingAPI = {
  getVMPricing: (providerId: number) => api.get(`/pricing/vms/${providerId}`),
  getOSPricing: (providerId: number) => api.get(`/pricing/os/${providerId}`),
  getAddonPricing: (providerId: number) => api.get(`/pricing/addons/${providerId}`),
  updateVMPrice: (id: number | string, data: object) => api.put(`/pricing/vms/${id}`, data),
  updateOSPrice: (id: number | string, data: object) => api.put(`/pricing/os/${id}`, data),
  updateAddonPrice: (id: number | string, data: object) => api.put(`/pricing/addons/${id}`, data),
  bulkUpdateVMPricing: (data: object) => api.post('/pricing/vms/bulk-update', data),
  getDiskPricing: (providerId: number) => api.get(`/pricing/disks?providerId=${providerId}`),
  updateDiskPrice: (id: number | string, data: object) => api.put(`/pricing/disks/${id}`, data),
  getDatabasePricing: (providerId: number) => api.get(`/pricing/databases?providerId=${providerId}`),
  updateDatabasePrice: (id: number | string, data: object) => api.put(`/pricing/databases/${id}`, data),
  getDatabaseEditionPricing: (providerId: number) => api.get(`/pricing/database-editions/${providerId}`),
  updateDatabaseEditionPrice: (id: number | string, data: object) => api.put(`/pricing/database-editions/${id}`, data),
  bulkUpdateDatabaseEditionPricing: (data: object) => api.post('/pricing/database-editions/bulk-update', data),
  getAddonOptionPricing: (providerId: number) => api.get(`/pricing/addon-options?providerId=${providerId}`),
  updateAddonOptionPrice: (id: number | string, data: object) => api.put(`/pricing/addon-options/${id}`, data),
  getDatabaseVMPricing: (providerId: number) => api.get(`/pricing/database-vms?providerId=${providerId}`),
  updateDatabaseVMPrice: (id: number | string, data: object) => api.put(`/pricing/database-vms/${id}`, data),
  recalculateDatabaseVMPricing: (providerId: number) => api.post(`/pricing/database-vms/recalculate/${providerId}`),
  getOtherItemsPricing: (providerId: number) => api.get(`/pricing/other-items?providerId=${providerId}`),
  updateOtherItemPrice: (id: number | string, data: object) => api.put(`/pricing/other-items/${id}`, data),
  getPowerVMPricing: (providerId: number) => api.get(`/pricing/power-vms/${providerId}`),
  updatePowerVMPrice: (id: number | string, data: object) => api.put(`/pricing/power-vms/${id}`, data),
  getPowerAddonPricing: (providerId: number) => api.get(`/pricing/power-addons/${providerId}`),
  updatePowerAddonPrice: (id: number | string, data: object) => api.put(`/pricing/power-addons/${id}`, data),
  getPowerAddonOptionPricing: (providerId: number) => api.get(`/pricing/power-addon-options?providerId=${providerId}`),
  updatePowerAddonOptionPrice: (id: number | string, data: object) => api.put(`/pricing/power-addon-options/${id}`, data),
  getPowerDiskPricing: (providerId: number) => api.get(`/pricing/power-disks?providerId=${providerId}`),
  updatePowerDiskPrice: (id: number | string, data: object) => api.put(`/pricing/power-disks/${id}`, data),
};


// Quotes
export const quotesAPI = {
  getAll: () => api.get('/quotes'),
  getById: (id: number | string) => api.get(`/quotes/${id}`),
  save: (data: object) => api.post('/quotes', data),
  delete: (id: number | string) => api.delete(`/quotes/${id}`),
};

// Disks
export const disksAPI = {
  getAll: () => api.get('/disks'),
  create: (data: object) => api.post('/disks', data),
  update: (id: number | string, data: object) => api.put(`/disks/${id}`, data),
  delete: (id: number | string) => api.delete(`/disks/${id}`)
};

// Database Types
export const databaseTypesAPI = {
  getAll: () => api.get('/database-types'),
  create: (data: object) => api.post('/database-types', data),
  update: (id: number | string, data: object) => api.put(`/database-types/${id}`, data),
  delete: (id: number | string) => api.delete(`/database-types/${id}`)
};

// Database Configurations (for Managed/PaaS databases)
export const databaseConfigsAPI = {
  getAll: (params?: object) => api.get('/database-configs', { params }),
  create: (data: object) => api.post('/database-configs', data),
  update: (id: number | string, data: object) => api.put(`/database-configs/${id}`, data),
  delete: (id: number | string) => api.delete(`/database-configs/${id}`)
};

// database API
export const databaseEditionsAPI = {
  getAll: (params?: object) => api.get('/database-editions', { params }),
  getById: (id: number | string) => api.get(`/database-editions/${id}`),
  getByDatabaseType: (typeId: number) => api.get(`/database-editions/type/${typeId}`),
  create: (data: object) => api.post('/database-editions', data),
  update: (id: number | string, data: object) => api.put(`/database-editions/${id}`, data),
  delete: (id: number | string) => api.delete(`/database-editions/${id}`)
};

// Addon Options
export const addonOptionsAPI = {
  getAll: () => api.get('/addon-options'),  // NEW: Get all addon options
  getByAddon: (addonId: number) => api.get(`/addon-options?addonId=${addonId}`),
  create: (data: object) => api.post('/addon-options', data),
  update: (id: number | string, data: object) => api.put(`/addon-options/${id}`, data),
  delete: (id: number | string) => api.delete(`/addon-options/${id}`)
};


// Other Items
export const otherItemsAPI = {
  getAll: () => api.get('/other-items'),
  create: (data: object) => api.post('/other-items', data),
  update: (id: number | string, data: object) => api.put(`/other-items/${id}`, data),
  delete: (id: number | string) => api.delete(`/other-items/${id}`)
};

// Power Types
export const powerTypesAPI = {
  getAll: () => api.get('/power-types'),
  getById: (id: number | string) => api.get(`/power-types/${id}`),
  create: (data: object) => api.post('/power-types', data),
  update: (id: number | string, data: object) => api.put(`/power-types/${id}`, data),
  delete: (id: number | string) => api.delete(`/power-types/${id}`),
};

// Power Addons
export const powerAddonsAPI = {
  getAll: () => api.get('/power-addons'),
  getById: (id: number | string) => api.get(`/power-addons/${id}`),
  create: (data: object) => api.post('/power-addons', data),
  update: (id: number | string, data: object) => api.put(`/power-addons/${id}`, data),
  delete: (id: number | string) => api.delete(`/power-addons/${id}`),
};

// Power Addon Options
export const powerAddonOptionsAPI = {
  getAll: () => api.get('/power-addon-options'),
  getByPowerAddon: (powerAddonId: number) => api.get(`/power-addon-options?powerAddonId=${powerAddonId}`),
  create: (data: object) => api.post('/power-addon-options', data),
  update: (id: number | string, data: object) => api.put(`/power-addon-options/${id}`, data),
  delete: (id: number | string) => api.delete(`/power-addon-options/${id}`),
};

// Power Default Addons
export const powerDefaultAddonsAPI = {
  getAll: (providerId?: number, regionId?: number) => api.get('/power-default-addons', { params: { providerId, regionId } }),
  batchSave: (updates: object[]) => api.post('/power-default-addons/batch', { updates }),
  delete: (id: number | string) => api.delete(`/power-default-addons/${id}`),
};

// API Mappings (SKU + Region mappings for cloud pricing import)
export const apiMappingsAPI = {
  getSKUMappings: (providerId: number) => api.get(`/api-mappings/sku/${providerId}`),
  getRegionMappings: (providerId: number) => api.get(`/api-mappings/regions/${providerId}`),
  saveSKUMappings: (providerId: number, mappings: Array<{ vmTypeId: number; externalSKU: string | null }>) =>
    api.put(`/api-mappings/sku/${providerId}`, { mappings }),
  saveRegionMappings: (providerId: number, mappings: Array<{ regionId: number; externalRegion: string | null }>) =>
    api.put(`/api-mappings/regions/${providerId}`, { mappings }),
  getSQLLicenseRates: (providerId: number) => api.get(`/api-mappings/sql-rates/${providerId}`),
  saveSQLLicenseRates: (providerId: number, rates: Array<{ databaseEditionId: number; costPerVCPU: number | null }>) =>
    api.put(`/api-mappings/sql-rates/${providerId}`, { rates }),
};

// Pricing Import (fetch cloud prices and apply)
export const pricingImportAPI = {
  getProviders: () => api.get('/pricing-import/providers'),
  getRegions: (providerId: number) => api.get(`/pricing-import/regions/${providerId}`),
  fetchPrices: (providerId: number, regionId: number) =>
    api.post('/pricing-import/fetch', { providerId, regionId }),
  applyPrices: (providerId: number, regionId: number, updates: Array<{ vmTypeId: number; osCategory: string; monthlyCost: number }>) =>
    api.post('/pricing-import/apply', { providerId, regionId, updates }),
  // DB Edition recalculation (for providers like IBM without external pricing APIs)
  getDbEditionProviders: () => api.get('/pricing-import/db-edition-providers'),
  getDbEditionRegions: (providerId: number) => api.get(`/pricing-import/db-edition-regions/${providerId}`),
  fetchDbEditionPrices: (providerId: number, regionId: number) =>
    api.post('/pricing-import/fetch-db-editions', { providerId, regionId }),
  applyDbEditionPrices: (providerId: number, regionId: number, updates: Array<{ vmTypeId: number; databaseEditionId: number; monthlyCost: number }>) =>
    api.post('/pricing-import/apply-db-editions', { providerId, regionId, updates }),
};

// Backup Pricing
export const backupPricingAPI = {
  getVMBackupPricing: (providerId: number) => api.get(`/backup-pricing/vms?providerId=${providerId}`),
  updateVMBackupPrice: (id: number | string, data: object) => api.put(`/backup-pricing/vms/${id}`, data),
  getDatabaseBackupPricing: (providerId: number) => api.get(`/backup-pricing/databases?providerId=${providerId}`),
  updateDatabaseBackupPrice: (id: number | string, data: object) => api.put(`/backup-pricing/databases/${id}`, data),
  getDatabaseExtraStoragePricing: (providerId: number) => api.get(`/backup-pricing/extra-storage?providerId=${providerId}`),
  updateDatabaseExtraStoragePrice: (id: number | string, data: object) => api.put(`/backup-pricing/extra-storage/${id}`, data),
};

export const drTestingPricingAPI = {
  getAll: (providerId: number) => api.get(`/dr-testing-pricing?providerId=${providerId}`),
  update: (id: number | string, data: object) => api.put(`/dr-testing-pricing/${id}`, data),
};

export default api;
